/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/anchor-is-valid */

/* eslint-disable import/no-cycle */

import React, { useState } from 'react';

import { object as YupObject } from 'yup';
import { Formik } from 'formik';

import Box from '@ntuctech/devex-tangram/Box';

import { useAuth0 } from '../../Auth0Provider';
import { useFlowControl } from '../../FlowControlProvider';
import { COUNTRY_CODE, FLOW_STEPS } from '../../../constants';

import PageContent from '../../theme/PageContent';
import PageHeader from '../../theme/PageHeader';
import FormDesc from '../../theme/FormDesc';
import UserTag from '../../theme/UserTag';
import ErrorBanner from '../../theme/ErrorBanner';
import BackButtonDesktop from '../../theme/BackButtonDesktop';

import InputValidator from '../../../services/input-validator';
import Api from '../../../services/api';
import Logger from '../../../services/logger';

import VerifyEmailForm from './VerifyEmailForm';

const gaScreenName = 'Verify Email';

const validationSchema = YupObject().shape({
  email: InputValidator.emailValidation({
    emptyEmailMessage: 'Email address is required.',
    invalidEmailMessage: 'Email address is not valid.',
    maxLengthMessage: 'Your email address should be within 64 characters.',
  }),
});

const VerifyEmail = () => {
  const { userContext, resetUserContext, passwordlessLogin, trackPwl } = useAuth0();
  const { goNext, reStart } = useFlowControl();

  const [error, setError] = useState(null);
  const [emailMatchRecords, setEmailMatchRecords] = useState({});

  const initialValues = {
    email: '',
  };

  const clearAndRestart = React.useCallback(() => {
    reStart();
    resetUserContext();
  }, [reStart, resetUserContext]);

  const handleMoveToPhoneSignup = () => {
    goNext(FLOW_STEPS.PHONE_SIGNUP);
  };

  React.useEffect(() => {
    trackPwl({
      screen_name: gaScreenName,
      event_action: 'Landed',
      event_label: 'SignIn_Verify_Email',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (values, actions) => {
    const { phoneNumber, verificationCode } = userContext;
    const { email } = values;

    try {
      if (Object.keys(emailMatchRecords).length) {
        trackPwl({
          screen_name: gaScreenName,
          event_action: 'Click',
          event_label: 'SignIn_Error_Email_Submit',
        });
      } else {
        trackPwl({
          screen_name: gaScreenName,
          event_action: 'Click',
          event_label: 'SignIn_Verify_Email_Submit',
        });
      }

      const responseData = await Api.verifyEmailRecordMatches({
        phoneNumber,
        verificationCode,
        email,
      });

      const { email_valid } = responseData;

      if (email_valid === false) {
        // return if email does not matches passwordless connected account
        setEmailMatchRecords({ ...emailMatchRecords, [email]: false });

        trackPwl({
          screen_name: gaScreenName,
          event_action: 'Click',
          event_label: 'SignIn_Error_Email_NotMatched',
        });

        return;
      }

      if (email_valid === true) {
        // sign in if email matches passwordless connected account
        // actions.setStatus({ emailMatched: { [email]: true } });
        setEmailMatchRecords({ ...emailMatchRecords, [email]: true });

        trackPwl({
          screen_name: gaScreenName,
          event_action: 'Click',
          event_label: 'SignIn_Verify_Email_Success',
        });

        // Complete passwordless authentication
        await new Promise((resolve) => {
          passwordlessLogin({ phoneNumber, verificationCode }, (err) => {
            if (err) {
              throw err;
            }
            resolve();
          });
        });
      } else {
        throw new Error('Request failed. Please try again.');
      }
    } catch (err) {
      setError(err);
      Logger.error(err);
    }

    actions.setSubmitting(false);
  };

  const userTag = `+${COUNTRY_CODE} ${userContext.phoneNumber}`;

  return (
    <>
      <BackButtonDesktop action={clearAndRestart} />
      <PageContent>
        <PageHeader header="Verify your account" backAction={clearAndRestart} />
        <UserTag content={userTag} data-testid="tag-userEmail" />
        <Box mt={-0.5}>
          <FormDesc>Please enter email to verify your account.</FormDesc>
        </Box>
        {error && <ErrorBanner error={error} />}
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {(formikProps) => (
            <VerifyEmailForm
              {...formikProps}
              handleMoveToPhoneSignup={handleMoveToPhoneSignup}
              emailMatchRecords={emailMatchRecords}
            />
          )}
        </Formik>
      </PageContent>
    </>
  );
};

export default VerifyEmail;
